import { displayText } from '../../../../../terminology/store';
import { useTerminology } from '../../../../../terminology/TerminologyContext';
import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ChromePicker } from 'react-color';
import { toast, ToastContainer } from 'react-toastify';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import DatePicker from 'react-datepicker';
import PhoneInput from 'react-phone-input-2';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-phone-input-2/lib/style.css';
import ThemeContext from '../../../../ThemeContext';
import './AddReseller.css';
import { basicSchema, priceSchema, formSchema } from '../../../../Utils/Validations';
import SuperadminConfig from '../../../../../config/Superadmin';

const AddReseller = () => {
  useTerminology();
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const location  = useLocation();

  // ── Pull everything from config ───────────
  const cfg          = SuperadminConfig.resellers;
  const routes       = SuperadminConfig.routes;
  const clientsCfg   = SuperadminConfig.clients;
  const reportsCfg   = SuperadminConfig.reports;
  const navCfg       = SuperadminConfig.nav;
  const featureFlags = SuperadminConfig.features;

  const currentUser  = JSON.parse(localStorage.getItem('user') || '{}') || {};
  const isSuperadmin = (currentUser.role || '').toLowerCase() === 'superadmin';
  const basePath     = isSuperadmin ? routes.base : '/admin';

  // Back-to-list path: basePath + routes.resellers (e.g. '/superadmin/advisors')
  // This MUST match the registered route in App.jsx. Changing routes.resellers
  // in SuperadminConfig automatically fixes this link.
  const listPath = `${basePath}/${routes.resellers}`;

  // ── Theme ──
  const fontFamily    = theme?.typography?.fontFamily || '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize  = Number(theme?.typography?.bodyFontSize)  || 14;
  const titleFontSize = Number(theme?.typography?.titleFontSize) || 24;

  const headingStyle = {
    fontFamily,
    fontSize: `${titleFontSize}px`,
    color: theme.colors.textPrimary,
    fontWeight: 'bold',
  };

  // ── Date helpers ─────────
  const formatDisplayDate = (date) => {
    if (!date) return '';
    return `${date.getDate().toString().padStart(2,'0')}-${(date.getMonth()+1).toString().padStart(2,'0')}-${date.getFullYear()}`;
  };
  const parseDisplayDate = (str, fallback) => {
    if (!str || str.length !== 10) return fallback;
    const [d, m, y] = str.split('-').map(Number);
    if (isNaN(d) || isNaN(m) || isNaN(y)) return fallback;
    const date = new Date(y, m - 1, d);
    return date.getDate() === d && date.getMonth() + 1 === m && date.getFullYear() === y ? date : fallback;
  };
  const formatAPIDate = (date) => {
    if (!date) return '';
    return `${date.getFullYear()}-${(date.getMonth()+1).toString().padStart(2,'0')}-${date.getDate().toString().padStart(2,'0')}`;
  };

  const emptyForm = (now = new Date()) => ({
    live_transcript_email: '', type: 'Scheduling', level: '', client_type: '',
    status: 'Active', video_conf: 'enable', reseller_name: '',
    start_date: formatAPIDate(now), sales_representative: '',
    primary_contact: '', primary_email: '', primary_phone: '',
    address_country: '', address: '', city: '', state: '', zip: '',
    reseller_description: '', banner_color: '#ffffff', text_color: '#333333',
    logo: null, url: '', subscription_fee: '',
    first_name: '', last_name: '', email: '', username: '', password: '', password_confirmation: '',
  });

  // ── State ──
  const [formData,       setFormData]       = useState(emptyForm());
  const [errors,         setErrors]         = useState({});
  const [activeTab,      setActiveTab]      = useState('basic');
  const [addressCountries, setAddressCountries] = useState([]);
  const [countryMap,     setCountryMap]     = useState({});
  const [statesList,     setStatesList]     = useState([]);
  const [isLoading,      setIsLoading]      = useState(false);
  const [showBannerColorPicker, setShowBannerColorPicker] = useState(false);
  const [showTextColorPicker,   setShowTextColorPicker]   = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [logoName,       setLogoName]       = useState('No file chosen');
  const [selectedDate,   setSelectedDate]   = useState(new Date());
  const [dateInputValue, setDateInputValue] = useState(formatDisplayDate(new Date()));
  const [phoneInputKey,  setPhoneInputKey]  = useState(0);

  // Top nav dropdown state
  const [isClientsDropdownOpen, setIsClientsDropdownOpen] = useState(false);
  const [isReportsDropdownOpen, setIsReportsDropdownOpen] = useState(false);
  const [isClientsPinned,       setIsClientsPinned]       = useState(false);
  const [isReportsPinned,       setIsReportsPinned]       = useState(false);
  const activeTopTab = 'resellers';

  const fileInputRef         = useRef(null);
  const textColorPickerRef   = useRef(null);
  const bannerColorPickerRef = useRef(null);
  const datePickerRef        = useRef(null);
  const iconRef              = useRef(null);
  const calendarRef          = useRef(null);
  const clientsDropdownRef   = useRef(null);
  const reportsDropdownRef   = useRef(null);

  // ── Date picker hover timer ───
  let hideTimer = null;
  const clearHideTimer = () => { if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; } };
  const startHideTimer = () => { clearHideTimer(); hideTimer = setTimeout(() => setShowDatePicker(false), 150); };

  // ── Admin-only tab list ───
  const ALL_ADMIN_TABS = ['resellers', 'clients', 'reports', 'account'];
  const adminTabs = ALL_ADMIN_TABS.filter((t) => {
    if (featureFlags?.tabs) return featureFlags.tabs[t] !== false;
    return true;
  });

  // ── Top-nav handlers ──────
  const handleTopTabClick = (tab) => {
    if (tab === 'clients') {
      setIsClientsPinned((p) => !p);
      setIsClientsDropdownOpen((p) => !p);
      setIsReportsPinned(false);
      setIsReportsDropdownOpen(false);
      return;
    }
    if (tab === 'reports') {
      setIsReportsPinned((p) => !p);
      setIsReportsDropdownOpen((p) => !p);
      setIsClientsPinned(false);
      setIsClientsDropdownOpen(false);
      return;
    }
    setIsClientsPinned(false);
    setIsClientsDropdownOpen(false);
    setIsReportsPinned(false);
    setIsReportsDropdownOpen(false);
    const map = {
      resellers : listPath,
      notify    : `${basePath}/${routes.notify}`,
      admins    : `${basePath}/${routes.admins}`,
      account   : `${basePath}/${routes.account}`,
    };
    if (map[tab]) navigate(map[tab]);
  };

  const handleClientSubTab = (subTab) => {
    navigate(
      subTab === 'enterprise'
        ? `${basePath}/${routes.clients.enterprise}`
        : `${basePath}/${routes.clients.core}`
    );
    setIsClientsDropdownOpen(false);
    setIsClientsPinned(false);
  };

  const handleReportSubTab = (subTab) => {
    navigate(`${basePath}/${routes.reports.base}?sub=${subTab}`);
    setIsReportsDropdownOpen(false);
    setIsReportsPinned(false);
  };

  // ── Form handlers ─────────
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target || {};
    let newValue = type === 'checkbox' ? checked : value;
    if (name === 'zip') newValue = value.replace(/[^0-9]/g, '');

    setFormData((prev) => {
      const updated = { ...prev, [name]: newValue };
      if ((name !== 'url' && name !== 'logo') || newValue) {
        try { formSchema.validateSyncAt(name, updated); setErrors((p) => ({ ...p, [name]: '' })); }
        catch (err) { setErrors((p) => ({ ...p, [name]: err.message })); }
      } else {
        setErrors((p) => ({ ...p, [name]: '' }));
      }
      return updated;
    });
    if (name === 'address_country') { setStatesList([]); setFormData((p) => ({ ...p, state: '' })); }
  };

  const handleDateInputChange = (e) => {
    const val = e.target.value;
    setDateInputValue(val);
    if (/^\d{2}-\d{2}-\d{4}$/.test(val)) {
      const parsed  = parseDisplayDate(val, selectedDate);
      const apiDate = formatAPIDate(parsed);
      try {
        formSchema.validateSyncAt('start_date', { ...formData, start_date: apiDate });
        setSelectedDate(parsed); setFormData((p) => ({ ...p, start_date: apiDate })); setErrors((p) => ({ ...p, start_date: '' }));
      } catch (err) { setErrors((p) => ({ ...p, start_date: err.message })); }
    } else {
      setErrors((p) => ({ ...p, start_date: 'Invalid format. Use DD-MM-YYYY' }));
    }
  };

  const handleDateInputBlur = () => {
    if (!dateInputValue) { setErrors((p) => ({ ...p, start_date: 'Date is required.' })); return; }
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateInputValue)) {
      const parsed  = parseDisplayDate(dateInputValue, selectedDate);
      const apiDate = formatAPIDate(parsed);
      try {
        formSchema.validateSyncAt('start_date', { ...formData, start_date: apiDate });
        setSelectedDate(parsed); setFormData((p) => ({ ...p, start_date: apiDate })); setErrors((p) => ({ ...p, start_date: '' }));
      } catch (err) { setDateInputValue(formatDisplayDate(selectedDate)); setErrors((p) => ({ ...p, start_date: err.message })); }
    } else {
      setDateInputValue(formatDisplayDate(selectedDate));
      setErrors((p) => ({ ...p, start_date: 'Invalid date format.' }));
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setDateInputValue(formatDisplayDate(date));
    setFormData((prev) => {
      const updated = { ...prev, start_date: formatAPIDate(date) };
      try { formSchema.validateSyncAt('start_date', updated); setErrors((p) => ({ ...p, start_date: '' })); }
      catch (err) { setErrors((p) => ({ ...p, start_date: err.message })); }
      return updated;
    });
    setShowDatePicker(false);
  };

  const validateTab = (tab) => {
    const tabSchemas = {
      basic: basicSchema.omit(['url', 'logo']),
      price: priceSchema,
      admin: formSchema.pick(['first_name','last_name','email','username','password','password_confirmation']),
    };
    let isValid = true;
    const newErrors = {};
    try {
      tabSchemas[tab].validateSync(formData, { abortEarly: false });
      if (formData.url)  { try { formSchema.validateSyncAt('url',  formData); newErrors.url  = ''; } catch (e) { newErrors.url  = e.message; isValid = false; } }
      if (formData.logo) { try { formSchema.validateSyncAt('logo', formData); newErrors.logo = ''; } catch (e) { newErrors.logo = e.message; isValid = false; } }
    } catch (err) {
      if (err.name === 'ValidationError') { err.inner.forEach((e) => { newErrors[e.path] = e.message; }); isValid = false; }
    }
    setErrors(newErrors);
    return isValid;
  };

  const validateAll = () => validateTab('basic') && validateTab('price') && validateTab('admin');

  const handleVideoConfChange = (value) => { setFormData((p) => ({ ...p, video_conf: value })); setErrors((p) => ({ ...p, video_conf: '' })); };
  const toggleTextColorPicker   = (e) => { e.preventDefault(); setShowTextColorPicker((p) => !p); setShowBannerColorPicker(false); };
  const toggleBannerColorPicker = (e) => { e.preventDefault(); setShowBannerColorPicker((p) => !p); setShowTextColorPicker(false); };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    const validTypes = ['image/jpeg','image/png','image/gif','image/bmp','image/webp'];
    if (!file) { setFormData((p) => ({ ...p, logo: null })); setLogoName('No file chosen'); setErrors((p) => ({ ...p, logo: '' })); return; }
    if (!validTypes.includes(file.type)) {
      setErrors((p) => ({ ...p, logo: 'The logo must be an image (JPEG, PNG, GIF, BMP, or WebP).' }));
      setFormData((p) => ({ ...p, logo: null })); setLogoName('No file chosen');
      if (fileInputRef.current) fileInputRef.current.value = null; return;
    }
    if (file.size > 2048 * 1024) {
      setErrors((p) => ({ ...p, logo: 'The logo must not exceed 2MB.' }));
      setFormData((p) => ({ ...p, logo: null })); setLogoName('No file chosen');
      if (fileInputRef.current) fileInputRef.current.value = null; return;
    }
    setFormData((p) => ({ ...p, logo: file })); setLogoName(file.name); setErrors((p) => ({ ...p, logo: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) { toast.error('Please fill all required fields correctly in all tabs.', { autoClose: 3000 }); return; }
    setIsLoading(true);
    const payload = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === 'logo')                     { if (formData.logo instanceof File) payload.append('logo', formData.logo); return; }
      if (key === 'video_conference_enabled') { return; }
      if (key === 'address_country')          { payload.append('country', formData[key]); return; }
      if (key === 'primary_phone' && formData[key]) {
        const pn = parsePhoneNumberFromString(formData[key]);
        payload.append(key, pn ? pn.number : formData[key]); return;
      }
      if (key === 'url' && !formData[key]) { return; }
      payload.append(key, formData[key]);
    });
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found. Please log in again.');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/webmanager/resellers`,
        payload,
        { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } }
      );
      setIsLoading(false);
      toast.success(response.data.message || `${cfg.singular} created successfully!`, {
        autoClose: 3000,
        // After save, navigate back to the list page (e.g. /superadmin/advisors)
        onClose: () => navigate(listPath),
      });
      const now = new Date();
      setFormData(emptyForm(now)); setSelectedDate(now); setDateInputValue(formatDisplayDate(now));
      setErrors({}); setLogoName('No file chosen');
      setPhoneInputKey((p) => p + 1);
      if (fileInputRef.current) fileInputRef.current.value = null;
    } catch (err) {
      setIsLoading(false);
      if (err.response?.status === 422) {
        const respErrors = err.response.data.errors || {};
        setErrors(respErrors);
        toast.error(Object.entries(respErrors).map(([f, m]) => `${f}: ${m.join(', ')}`).join('\n') || 'Validation failed.', { autoClose: 5000 });
      } else if (err.response?.status === 401) {
        toast.error('Session expired or invalid token. Please log in again.', { autoClose: 3000 });
        setTimeout(() => navigate('/login'), 3000);
      } else {
        toast.error(err.response?.data?.message || 'An error occurred while submitting the form.', { autoClose: 3000 });
      }
    }
  };

  // Cancel → back to list (e.g. /superadmin/advisors)
  const handleCancel = () => {
    const now = new Date();
    setFormData(emptyForm(now)); setSelectedDate(now); setDateInputValue(formatDisplayDate(now));
    setErrors({}); setLogoName('No file chosen');
    setPhoneInputKey((p) => p + 1);
    navigate(listPath);
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (!validateTab(activeTab)) { toast.error('Please fill all required fields correctly.', { autoClose: 3000 }); return; }
    if (activeTab === 'basic') setActiveTab('price');
    else if (activeTab === 'price') setActiveTab('admin');
    setErrors({});
  };

  const handlePrevious = (e) => {
    e.preventDefault();
    if (activeTab === 'price') setActiveTab('basic');
    else if (activeTab === 'admin') setActiveTab('price');
    setErrors({});
  };

  // ── Effects ─
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res  = await fetch('https://countriesnow.space/api/v0.1/countries');
        const data = await res.json();
        if (data.error) throw new Error('Failed to fetch countries');
        const sorted = data.data.sort((a, b) => a.country.localeCompare(b.country));
        setAddressCountries(sorted.map((c) => ({ name: c.country, code: c.iso2 })));
        const map = {};
        data.data.forEach((c) => { map[c.iso2] = c.country; });
        setCountryMap(map);
      } catch {
        toast.error('Failed to load countries.', { autoClose: 3000 });
        setAddressCountries([]); setCountryMap({});
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      if (!formData.address_country) { setStatesList([]); return; }
      try {
        const res  = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ country: formData.address_country }),
        });
        const data = await res.json();
        if (data.error || !data.data) throw new Error('Failed');
        setStatesList((data.data.states || []).map((s) => s.name).sort());
      } catch {
        setStatesList([]);
        toast.warn(`No states found for ${formData.address_country}.`, { autoClose: 3000 });
      }
    };
    fetchStates();
  }, [formData.address_country]);

  useEffect(() => {
    return () => {
      if (formData.logo) { try { URL.revokeObjectURL(URL.createObjectURL(formData.logo)); } catch {} }
      clearHideTimer();
    };
  }, [formData.logo]);

  useEffect(() => {
    const handle = (event) => {
      if (textColorPickerRef.current   && !textColorPickerRef.current.contains(event.target))   setShowTextColorPicker(false);
      if (bannerColorPickerRef.current && !bannerColorPickerRef.current.contains(event.target)) setShowBannerColorPicker(false);
      if (datePickerRef.current        && !datePickerRef.current.contains(event.target))        setShowDatePicker(false);
      if (clientsDropdownRef.current   && !clientsDropdownRef.current.contains(event.target)) {
        setIsClientsDropdownOpen(false); setIsClientsPinned(false);
      }
      if (reportsDropdownRef.current   && !reportsDropdownRef.current.contains(event.target)) {
        setIsReportsDropdownOpen(false); setIsReportsPinned(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // ── Dropdown item style helper 
  const getDropdownItemStyle = () => ({
    width: '100%', padding: '0.75rem 1rem', textAlign: 'left',
    background: 'transparent', color: theme.colors.textPrimary,
    border: 'none', cursor: 'pointer',
    fontSize: `${bodyFontSize}px`, fontFamily,
    transition: 'all 0.2s ease',
  });

  // ── Render ──
  return (
    <div
      className="add-reseller-container"
      style={{ fontFamily, fontSize: `${bodyFontSize}px`, color: theme.colors.textPrimary }}
    >
      <ToastContainer />
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner" style={{ border: `4px solid ${theme.colors.primary}`, borderTop: '4px solid transparent' }} />
        </div>
      )}

      {/* ── Top Nav (admin only — superadmin has its own dashboard nav) ── */}
      {!isSuperadmin && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', justifyContent: 'center', flexWrap: 'wrap', fontFamily }}>
          {adminTabs.map((tab) => {
            const isClientsTab = tab === 'clients';
            const isReportsTab = tab === 'reports';
            const hasDropdown  = isClientsTab || isReportsTab;
            const tabLabel     = navCfg[tab]?.label || (tab.charAt(0).toUpperCase() + tab.slice(1));
            return (
              <div
                key={tab}
                style={{ position: 'relative', flex: '1 1 0', minWidth: '100px' }}
                ref={isClientsTab ? clientsDropdownRef : isReportsTab ? reportsDropdownRef : null}
                onMouseEnter={() => {
                  if (isClientsTab) { setIsClientsDropdownOpen(true); }
                  if (isReportsTab) { setIsReportsDropdownOpen(true); }
                }}
                onMouseLeave={() => {
                  if (isClientsTab && !isClientsPinned) setIsClientsDropdownOpen(false);
                  if (isReportsTab && !isReportsPinned) setIsReportsDropdownOpen(false);
                }}
              >
                <button
                  onClick={() => handleTopTabClick(tab)}
                  style={{
                    width: '100%', padding: '0.5rem', borderRadius: '6px', border: 'none',
                    fontWeight: 'bold',
                    background: tab === activeTopTab ? theme.gradients.primary : theme.gradients.secondary,
                    color: '#fff', cursor: 'pointer', fontFamily,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                  }}
                >
                  {navCfg[tab]?.icon && (
                    <i className={`bi bi-${navCfg[tab].icon}`} style={{ fontSize: `${Math.max(bodyFontSize - 2, 10)}px` }} />
                  )}
                  <span>{tabLabel}</span>
                  {hasDropdown && <i className="bi bi-chevron-down" style={{ fontSize: '12px' }} />}
                </button>

                {/* Clients dropdown */}
                {isClientsTab && isClientsDropdownOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0,
                    backgroundColor: theme.colors.bgPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                    borderRadius: '6px', boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                    zIndex: 30, width: '100%', minWidth: '160px', overflow: 'hidden', fontFamily,
                  }}>
                    {featureFlags?.clients?.core !== false && (
                      <button style={getDropdownItemStyle()} onClick={() => handleClientSubTab('core')}
                        onMouseOver={(e) => (e.currentTarget.style.background = theme.gradients.secondary)}
                        onMouseOut={(e)  => (e.currentTarget.style.background = 'transparent')}>
                        {clientsCfg.core.label}
                      </button>
                    )}
                    {featureFlags?.clients?.core !== false && featureFlags?.clients?.enterprise !== false && (
                      <hr style={{ margin: 0, border: 'none', borderTop: `1px solid ${theme.colors.borderColor}`, opacity: 0.5 }} />
                    )}
                    {featureFlags?.clients?.enterprise !== false && (
                      <button style={getDropdownItemStyle()} onClick={() => handleClientSubTab('enterprise')}
                        onMouseOver={(e) => (e.currentTarget.style.background = theme.gradients.secondary)}
                        onMouseOut={(e)  => (e.currentTarget.style.background = 'transparent')}>
                        {clientsCfg.enterprise.label}
                      </button>
                    )}
                  </div>
                )}

                {/* Reports dropdown */}
                {isReportsTab && isReportsDropdownOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0,
                    backgroundColor: theme.colors.bgPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                    borderRadius: '6px', boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                    zIndex: 30, width: '100%', minWidth: '180px', overflow: 'hidden', fontFamily,
                  }}>
                    {Object.entries(reportsCfg)
                      .filter(([key]) => featureFlags?.reports?.[key] !== false)
                      .map(([key, item], idx, arr) => (
                        <React.Fragment key={key}>
                          <button style={getDropdownItemStyle()} onClick={() => handleReportSubTab(key)}
                            onMouseOver={(e) => (e.currentTarget.style.background = theme.gradients.secondary)}
                            onMouseOut={(e)  => (e.currentTarget.style.background = 'transparent')}>
                            {item.label}
                          </button>
                          {idx < arr.length - 1 && (
                            <hr style={{ margin: 0, border: 'none', borderTop: `1px solid ${theme.colors.borderColor}`, opacity: 0.5 }} />
                          )}
                        </React.Fragment>
                      ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Breadcrumb ──
          The Link uses listPath = basePath + routes.resellers.
          e.g. '/superadmin/advisors' — matches the registered route in App.jsx.
          Clicking this will navigate back to the Advisors list page, NOT close/logout.
      ── */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <Link to={listPath} style={{ color: theme.colors.primary, textDecoration: 'underline' }}>
            {cfg.plural} Management
          </Link>
          <span style={{ margin: '0 0.5rem', color: theme.colors.textPrimary }}>&gt;</span>
          <span style={{ color: theme.colors.textPrimary }}>Add New {cfg.singular}</span>
        </div>
      </div>

      {/* ── Main Container ── */}
      <div style={{ backgroundColor: theme.colors.bgPrimary, borderRadius: '12px', padding: '1.5rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <h4 style={headingStyle}>Add New {cfg.singular}</h4>

        {/* Tab Controls */}
        <div style={{ display: 'flex', gap: '2rem', marginBottom: '1.5rem' }}>
          {[{ key: 'basic', label: 'Basic Information' }, { key: 'price', label: 'Price Package' }, { key: 'admin', label: 'Admin Setup' }].map((tab, index) => (
            <a
              key={tab.key} href="#"
              style={{ color: activeTab === tab.key ? theme.colors.primary : theme.colors.secondary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              onClick={(e) => { e.preventDefault(); if (validateTab(activeTab)) { setActiveTab(tab.key); setErrors({}); } }}
            >
              <span style={{
                border: `2px solid ${activeTab === tab.key ? theme.colors.primary : theme.colors.secondary}`,
                color: activeTab === tab.key ? theme.colors.textWhite : theme.colors.secondary,
                backgroundColor: activeTab === tab.key ? theme.colors.primary : 'transparent',
                width: '28px', height: '28px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold',
              }}>
                {index + 1}
              </span>
              <span style={{ borderBottom: activeTab === tab.key ? `2px solid ${theme.colors.primary}` : 'none', paddingBottom: '4px' }}>
                {tab.label}
              </span>
            </a>
          ))}
        </div>

        {/* ══════════════ BASIC TAB ══════════════ */}
        {activeTab === 'basic' && (
          <div className="tab-content">
            <div className="card-container theme-panel" style={{ backgroundColor: theme.colors.bgPrimary, border: `1px solid ${theme.colors.borderColor}`, boxShadow: theme.shadows?.medium || 'none', width: '100%', padding: '1.5rem' }}>

              <h5 style={headingStyle}>{cfg.singular} Default Settings</h5>

              <div className="form-group form-group--spaced">
                <div className="input-container">
                  <label className="input-label">Live Transcripts Email</label>
                  <input type="email" name="live_transcript_email" value={formData.live_transcript_email} onChange={handleInputChange}
                    className={`input-field ${errors.live_transcript_email ? 'input-error' : ''}`}
                    style={{ border: `1px solid ${errors.live_transcript_email ? theme.colors.danger : theme.colors.borderColor}` }} />
                  {errors.live_transcript_email && <span className="error-text">{errors.live_transcript_email}</span>}
                </div>
                <div className="input-container" /><div className="input-container" />
              </div>

              <div className="form-group form-group--spaced">
                <div className="input-container">
                  <label className="input-label">{cfg.singular} Type</label>
                  <select name="type" value={formData.type} onChange={handleInputChange}
                    className={`select-field ${errors.type ? 'input-error' : ''}`}
                    style={{ border: `1px solid ${errors.type ? theme.colors.danger : theme.colors.borderColor}` }}>
                    <option value="Scheduling">Scheduling {cfg.singular}</option>
                    <option value="eDiscovery">eDiscovery {cfg.singular}</option>
                    <option value="Demo">Demo {cfg.singular}</option>
                    <option value="Standard">Standard {cfg.singular}</option>
                  </select>
                  {errors.type && <span className="error-text">{errors.type}</span>}
                </div>
                <div className="input-container">
                  <label className="input-label">{cfg.singular} Level</label>
                  <select name="level" value={formData.level} onChange={handleInputChange}
                    className={`select-field ${errors.level ? 'input-error' : ''}`}
                    style={{ border: `1px solid ${errors.level ? theme.colors.danger : theme.colors.borderColor}` }}>
                    <option value="">None</option>
                    <option value="Platinum">Platinum</option>
                    <option value="Gold">Gold</option>
                    <option value="Silver">Silver</option>
                    <option value="Bronze">Bronze</option>
                    <option value="None">None</option>
                  </select>
                  {errors.level && <span className="error-text">{errors.level}</span>}
                </div>
                <div className="input-container">
                  <label className="input-label">{displayText("Client Type")}</label>
                  <select name="client_type" value={formData.client_type} onChange={handleInputChange}
                    className={`select-field ${errors.client_type ? 'input-error' : ''}`}
                    style={{ border: `1px solid ${errors.client_type ? theme.colors.danger : theme.colors.borderColor}` }}>
                    <option value="R">R</option>
                    <option value="OC">OC</option>
                  </select>
                  {errors.client_type && <span className="error-text">{errors.client_type}</span>}
                </div>
              </div>

              <h5 className="section-title section-title--spaced" style={headingStyle}>{cfg.singular} Settings</h5>
              <div className="form-group form-group--spaced">
                <div className="input-container">
                  <label className="input-label">Status</label>
                  <div className="radio-group">
                    {['Active', 'Deactivated'].map((s) => (
                      <label key={s} className="radio-label">
                        <input type="radio" name="status" value={s} checked={formData.status === s} onChange={handleInputChange} /> {s}
                      </label>
                    ))}
                  </div>
                  {errors.status && <span className="error-text">{errors.status}</span>}
                </div>
                <div className="input-container" /><div className="input-container" />
              </div>

              <h5 className="section-title section-title--spaced" style={headingStyle}>Video Conference</h5>
              <div className="form-group form-group--spaced">
                <div className="input-container">
                  <div className="radio-group">
                    {[{ val: 'enable', label: 'Enabled' }, { val: 'disable', label: 'Disabled' }].map(({ val, label }) => (
                      <label key={val} className="radio-label">
                        <input type="radio" name="video_conf" value={val} checked={formData.video_conf === val} onChange={() => handleVideoConfChange(val)} /> {label}
                      </label>
                    ))}
                  </div>
                  {errors.video_conf && <span className="error-text">{errors.video_conf}</span>}
                </div>
                <div className="input-container" /><div className="input-container" />
              </div>

              <div className="form-group form-group--spaced">
                <div className="input-container">
                  <label className="input-label">{cfg.singular} Name*</label>
                  <input type="text" name="reseller_name" value={formData.reseller_name} onChange={handleInputChange} required
                    className={`input-field ${errors.reseller_name ? 'input-error' : ''}`}
                    style={{ border: `1px solid ${errors.reseller_name ? theme.colors.danger : theme.colors.borderColor}` }} />
                  {errors.reseller_name && <span className="error-text">{errors.reseller_name}</span>}
                </div>
                <div className="input-container">
                  <label className="input-label">Start Date*</label>
                  <div className="date-input-container" style={{ position: 'relative', border: `1px solid ${errors.start_date ? theme.colors.danger : theme.colors.borderColor}`, borderRadius: '0.25rem', padding: '0.375rem 0.75rem', backgroundColor: '#fff' }} ref={datePickerRef}>
                    <input type="text" name="start_date_input" value={dateInputValue} onChange={handleDateInputChange} onBlur={handleDateInputBlur}
                      className={`input-field ${errors.start_date ? 'input-error' : ''}`}
                      style={{ border: 'none', width: '100%', padding: '0', background: 'transparent' }} placeholder="DD-MM-YYYY" />
                    <span ref={iconRef} onMouseEnter={() => { clearHideTimer(); setShowDatePicker(true); }} onMouseLeave={() => startHideTimer()} style={{ display: 'inline-block' }}>
                      <i className="bi bi-calendar" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: theme.colors.textPrimary, cursor: 'pointer' }}
                        onClick={() => { clearHideTimer(); setShowDatePicker((p) => !p); }} />
                    </span>
                    {showDatePicker && (
                      <div ref={calendarRef} onMouseEnter={() => { clearHideTimer(); setShowDatePicker(true); }} onMouseLeave={() => startHideTimer()} style={{ position: 'absolute', top: '100%', left: 0, zIndex: 10 }}>
                        <DatePicker selected={selectedDate} onChange={handleDateChange} dateFormat="dd-MM-yyyy" inline className="custom-date-picker" />
                      </div>
                    )}
                  </div>
                  {errors.start_date && <span className="error-text">{errors.start_date}</span>}
                </div>
                <div className="input-container">
                  <label className="input-label">Sales Representative</label>
                  <input type="text" name="sales_representative" value={formData.sales_representative} onChange={handleInputChange}
                    className={`input-field ${errors.sales_representative ? 'input-error' : ''}`}
                    style={{ border: `1px solid ${errors.sales_representative ? theme.colors.danger : theme.colors.borderColor}` }} />
                  {errors.sales_representative && <span className="error-text">{errors.sales_representative}</span>}
                </div>
              </div>

              <div className="form-group form-group--spaced">
                {[
                  { label: 'Primary Contact (Name)*',  name: 'primary_contact', type: 'text'  },
                  { label: 'Primary Contact (Email)*', name: 'primary_email',   type: 'email' },
                ].map(({ label, name, type }) => (
                  <div key={name} className="input-container">
                    <label className="input-label">{label}</label>
                    <input type={type} name={name} value={formData[name]} onChange={handleInputChange} required
                      className={`input-field ${errors[name] ? 'input-error' : ''}`}
                      style={{ border: `1px solid ${errors[name] ? theme.colors.danger : theme.colors.borderColor}` }} />
                    {errors[name] && <span className="error-text">{errors[name]}</span>}
                  </div>
                ))}
                <div className="input-container">
                  <label className="input-label">Primary Contact (Mobile)*</label>
                  <PhoneInput
                    key={phoneInputKey} country={'us'} value={formData.primary_phone}
                    onChange={(value) => { const p = value.startsWith('+') ? value : `+${value}`; setFormData((prev) => ({ ...prev, primary_phone: p })); if (errors.primary_phone) setErrors((prev) => ({ ...prev, primary_phone: '' })); }}
                    onBlur={() => {
                      if (!formData.primary_phone) { setErrors((p) => ({ ...p, primary_phone: 'Mobile is required.' })); return; }
                      try { const pn = parsePhoneNumberFromString(formData.primary_phone); if (!pn || !pn.isValid()) setErrors((p) => ({ ...p, primary_phone: 'Invalid mobile number.' })); else setErrors((p) => ({ ...p, primary_phone: '' })); }
                      catch { setErrors((p) => ({ ...p, primary_phone: 'Invalid mobile number.' })); }
                    }}
                    inputClass={`form-control ${errors.primary_phone ? 'is-invalid' : ''}`}
                    inputStyle={{ width: '100%', height: '35px', fontSize: '12px', fontFamily: 'inherit', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, border: `1px solid ${errors.primary_phone ? theme.colors.danger : theme.colors.borderColor}`, borderRadius: '4px', color: '#666', padding: '6px 10px 6px 45px' }}
                    containerStyle={{ width: '100%' }}
                    inputProps={{ name: 'primary_phone', required: true }}
                    buttonStyle={{ border: `1px solid ${theme.colors.borderColor}`, backgroundColor: theme.colors.bgPrimary, borderRight: 'none', borderRadius: '4px 0 0 4px', height: '35px' }}
                  />
                  {errors.primary_phone && <div className="invalid-feedback d-block" style={{ color: theme.colors.danger, display: 'block', width: '100%', marginTop: '0.25rem', fontSize: '0.875em' }}>{errors.primary_phone}</div>}
                </div>
              </div>

              <div className="form-group">
                <div className="input-container">
                  <label className="input-label">Country*</label>
                  <select name="address_country" value={formData.address_country} onChange={handleInputChange} required
                    className={`select-field ${errors.address_country ? 'input-error' : ''}`}
                    style={{ border: `1px solid ${errors.address_country ? theme.colors.danger : theme.colors.borderColor}` }}>
                    <option value="">Select Country</option>
                    {addressCountries.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}
                  </select>
                  {errors.address_country && <span className="error-text">{errors.address_country}</span>}
                </div>
              </div>
              <div className="form-group">
                <div className="input-container">
                  <label className="input-label">Address*</label>
                  <input type="text" name="address" value={formData.address} onChange={handleInputChange} required
                    className={`input-field ${errors.address ? 'input-error' : ''}`}
                    style={{ border: `1px solid ${errors.address ? theme.colors.danger : theme.colors.borderColor}` }} />
                  {errors.address && <span className="error-text">{errors.address}</span>}
                </div>
              </div>
              <div className="form-group form-group--spaced">
                <div className="input-container">
                  <label className="input-label">City*</label>
                  <input type="text" name="city" value={formData.city} onChange={handleInputChange} required
                    className={`input-field ${errors.city ? 'input-error' : ''}`}
                    style={{ border: `1px solid ${errors.city ? theme.colors.danger : theme.colors.borderColor}` }} />
                  {errors.city && <span className="error-text">{errors.city}</span>}
                </div>
                <div className="input-container">
                  <label className="input-label">State*</label>
                  <select name="state" value={formData.state} onChange={handleInputChange} disabled={!formData.address_country} required
                    className={`select-field ${errors.state ? 'input-error' : ''}`}
                    style={{ border: `1px solid ${errors.state ? theme.colors.danger : theme.colors.borderColor}` }}>
                    <option value="">Select State</option>
                    {statesList.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.state && <span className="error-text">{errors.state}</span>}
                </div>
                <div className="input-container">
                  <label className="input-label">ZIP*</label>
                  <input type="text" name="zip" value={formData.zip} onChange={handleInputChange} required
                    className={`input-field ${errors.zip ? 'input-error' : ''}`}
                    style={{ border: `1px solid ${errors.zip ? theme.colors.danger : theme.colors.borderColor}` }} />
                  {errors.zip && <span className="error-text">{errors.zip}</span>}
                </div>
              </div>

              <h5 className="section-title section-title--spaced" style={headingStyle}>Branding Info</h5>
              <div className="form-group">
                <div className="input-container">
                  <label className="input-label">{cfg.singular} Description</label>
                  <textarea name="reseller_description" value={formData.reseller_description} onChange={handleInputChange} rows="3"
                    className={`textarea-field ${errors.reseller_description ? 'input-error' : ''}`}
                    style={{ border: `1px solid ${errors.reseller_description ? theme.colors.danger : theme.colors.borderColor}` }} />
                  {errors.reseller_description && <span className="error-text">{errors.reseller_description}</span>}
                </div>
                <div className="input-container">
                  <label className="input-label">Layout Preview</label>
                  <div className="layout-preview" style={{ backgroundColor: formData.banner_color, border: `1px solid ${theme.colors.borderColor}` }}>
                    {formData.logo && <img src={URL.createObjectURL(formData.logo)} alt="Logo Preview" className="logo-preview" style={{ border: `1px solid ${formData.text_color}` }} />}
                    <div>
                      <p className="sample-text" style={{ color: formData.text_color }}>Sample Text</p>
                      <div className="color-picker-buttons">
                        <button className={`color-picker-button ${showTextColorPicker ? 'active' : ''}`} onClick={toggleTextColorPicker} style={{ background: showTextColorPicker ? theme.gradients.primary : theme.gradients.secondary, color: theme.colors.textWhite }}>Change Text Color</button>
                        {showTextColorPicker && <div ref={textColorPickerRef} className="color-picker text-color-picker"><ChromePicker color={formData.text_color} onChange={(c) => setFormData((p) => ({ ...p, text_color: c.hex }))} styles={{ default: { picker: { width: '180px' } } }} /></div>}
                        <button className={`color-picker-button ${showBannerColorPicker ? 'active' : ''}`} onClick={toggleBannerColorPicker} style={{ background: showBannerColorPicker ? theme.gradients.primary : theme.gradients.secondary, color: theme.colors.textWhite }}>Change Banner Color</button>
                        {showBannerColorPicker && <div ref={bannerColorPickerRef} className="color-picker banner-color-picker"><ChromePicker color={formData.banner_color} onChange={(c) => setFormData((p) => ({ ...p, banner_color: c.hex }))} styles={{ default: { picker: { width: '180px' } } }} /></div>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <div className="input-container">
                  <label className="input-label">Website URL</label>
                  <input type="url" name="url" value={formData.url} onChange={handleInputChange} placeholder="https://example.com"
                    className={`input-field ${errors.url ? 'input-error' : ''}`}
                    style={{ border: `1px solid ${errors.url ? theme.colors.danger : theme.colors.borderColor}` }} />
                  {errors.url && <span className="error-text">{errors.url}</span>}
                </div>
                <div className="input-container">
                  <label className="input-label">{cfg.singular} Logo</label>
                  <div className="file-input-container" style={{ border: `1px solid ${errors.logo ? theme.colors.danger : theme.colors.borderColor}`, display: 'flex', alignItems: 'center', padding: '0.375rem 0.75rem', borderRadius: '0.25rem', backgroundColor: '#fff' }}>
                    <label htmlFor="logo-upload" style={{ background: theme.gradients.primary, color: theme.colors.textWhite, padding: '0.25rem 0.5rem', cursor: 'pointer', borderRadius: '0.25rem', marginLeft: '-8px', marginRight: '10px', whiteSpace: 'nowrap' }}>Browse</label>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{logoName}</span>
                  </div>
                  <input type="file" id="logo-upload" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" style={{ display: 'none' }} />
                  {errors.logo && <span className="error-text">{errors.logo}</span>}
                </div>
                <div className="input-container" />
              </div>

              <div className="form-actions" style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <button onClick={handleCancel} style={{ background: theme.gradients.secondary, color: theme.colors.textWhite, padding: '0.6rem 1.2rem', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}>Cancel</button>
                <button onClick={handleNext}   style={{ background: theme.gradients.primary,   color: theme.colors.textWhite, padding: '0.6rem 1.2rem', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}>Next</button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════ PRICE TAB ══════════════ */}
        {activeTab === 'price' && (
          <div className="tab-content">
            <div className="card-container theme-panel" style={{ backgroundColor: theme.colors.bgPrimary, border: `1px solid ${theme.colors.borderColor}`, boxShadow: theme.shadows?.medium || 'none', width: '100%', padding: '1.5rem' }}>
              <h5 style={headingStyle}>Define {cfg.singular} Price Package</h5>
              <h6 className="section-subtitle">Subscription Fee (Per User)</h6>
              <div className="form-group">
                <div className="input-container">
                  <label className="input-label">Subscription Fee*</label>
                  <div className="currency-input" style={{ display: 'flex', alignItems: 'center', border: `1px solid ${errors.subscription_fee ? theme.colors.danger : theme.colors.borderColor}`, borderRadius: '0.25rem', overflow: 'hidden' }}>
                    <span className="currency-symbol" style={{ padding: '0.375rem 0.75rem', backgroundColor: '#f9f9f9', color: theme.colors.textPrimary }}>$</span>
                    <input type="number" name="subscription_fee" value={formData.subscription_fee} onChange={handleInputChange} placeholder="Enter amount" required
                      className={`input-field ${errors.subscription_fee ? 'input-error' : ''}`}
                      style={{ border: 'none', padding: '0.375rem 0.75rem', flex: 1, height: '2.25rem' }} />
                  </div>
                  {errors.subscription_fee && <span className="error-text">{errors.subscription_fee}</span>}
                </div>
              </div>
              <div className="form-actions" style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <button onClick={handlePrevious} style={{ background: theme.gradients.secondary, color: theme.colors.textWhite, padding: '0.6rem 1.2rem', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}>Previous</button>
                <button onClick={handleNext}     style={{ background: theme.gradients.primary,   color: theme.colors.textWhite, padding: '0.6rem 1.2rem', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}>Next</button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════ ADMIN TAB ══════════════ */}
        {activeTab === 'admin' && (
          <div className="tab-content">
            <div className="card-container theme-panel" style={{ backgroundColor: theme.colors.bgPrimary, border: `1px solid ${theme.colors.borderColor}`, boxShadow: theme.shadows?.medium || 'none', width: '100%', padding: '1.5rem' }}>
              <h5 style={headingStyle}>Admin Details</h5>
              <div className="form-group form-group--spaced">
                {[
                  { label: 'First Name*',  name: 'first_name', type: 'text'  },
                  { label: 'Last Name*',   name: 'last_name',  type: 'text'  },
                  { label: 'Email*',       name: 'email',      type: 'email' },
                ].map(({ label, name, type }) => (
                  <div key={name} className="input-container">
                    <label className="input-label">{label}</label>
                    <input type={type} name={name} value={formData[name]} onChange={handleInputChange} required
                      className={`input-field ${errors[name] ? 'input-error' : ''}`}
                      style={{ border: `1px solid ${errors[name] ? theme.colors.danger : theme.colors.borderColor}` }} />
                    {errors[name] && <span className="error-text">{errors[name]}</span>}
                  </div>
                ))}
              </div>
              <div className="form-group form-group--spaced">
                {[
                  { label: 'Username*',         name: 'username',              type: 'text'     },
                  { label: 'Password*',         name: 'password',              type: 'password' },
                  { label: 'Confirm Password*', name: 'password_confirmation', type: 'password' },
                ].map(({ label, name, type }) => (
                  <div key={name} className="input-container">
                    <label className="input-label">{label}</label>
                    <input type={type} name={name} value={formData[name]} onChange={handleInputChange} required
                      className={`input-field ${errors[name] ? 'input-error' : ''}`}
                      style={{ border: `1px solid ${errors[name] ? theme.colors.danger : theme.colors.borderColor}` }} />
                    {errors[name] && <span className="error-text">{errors[name]}</span>}
                  </div>
                ))}
              </div>
              <div className="form-actions" style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <button onClick={handlePrevious} style={{ background: theme.gradients.secondary, color: theme.colors.textWhite, padding: '0.6rem 1.2rem', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}>Previous</button>
                <button onClick={handleSubmit} disabled={isLoading} style={{ background: theme.gradients.primary, color: theme.colors.textWhite, padding: '0.6rem 1.2rem', border: 'none', borderRadius: '0.25rem', cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.6 : 1 }}>
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddReseller;